import {
  useCreateQBankStructureMutation,
  useGetQBankStructureLevelsByQBankStructureIdLazyQuery,
  useUpdateQBankStructureLevelsByQBankStructureIdMutation,
  type CreateQBankStructureInput,
} from '@exam-management/graphql';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useState, useCallback, useMemo } from 'react';

import { CreateQBankStructureModal, SetupQBankStructureLevelModal } from '../../containers';

import type { QBankStructureLevel, QBankStructureCreationModalContextData } from './types';

enum QBankStructureCreationModalAction {
  CREATE_SECTION = 'CREATE_SECTION',
  SETUP_SECTION = 'SETUP_SECTION',
}

const QBankStructureCreationModalContext =
  createContext<QBankStructureCreationModalContextData | null>(null);

type QBankStructureCreationModalActionProviderProps = PropsWithChildren;

function QBankStructureCreationModalActionProvider(
  props: Readonly<QBankStructureCreationModalActionProviderProps>,
) {
  const { children } = props;
  const [action, setAction] = useState<QBankStructureCreationModalAction | null>(null);
  const [qBankStructureId, setQBankStructureId] = useState<string | null>(null);

  const createSection = useCallback(() => {
    setAction(QBankStructureCreationModalAction.CREATE_SECTION);
  }, [setAction]);

  const setupSection = useCallback(() => {
    setAction(QBankStructureCreationModalAction.SETUP_SECTION);
  }, [setAction]);

  const [
    getQBankStructureLevel,
    { data: getQBankStructureLevelResult, loading: isGetQBankStructureLevelLoading },
  ] = useGetQBankStructureLevelsByQBankStructureIdLazyQuery();

  const [createQBankStructure, { loading: isCreateQBankStructureLoading }] =
    useCreateQBankStructureMutation();

  const submitCreateQBankStructureForm = useCallback(
    async (formValues: CreateQBankStructureInput) => {
      const qBankStructure = await createQBankStructure({
        variables: {
          input: formValues,
        },
      });

      setQBankStructureId(qBankStructure.data?.qbsCreateQBankStructure.id ?? null);

      await getQBankStructureLevel({
        variables: {
          qBankStructureId: qBankStructure.data?.qbsCreateQBankStructure.id ?? '',
        },
      });
    },
    [createQBankStructure, getQBankStructureLevel, setQBankStructureId],
  );

  const qBankStructureLevels = useMemo(
    () => getQBankStructureLevelResult?.qbsGetQBankStructureLevelsByQBankStructureId ?? [],
    [getQBankStructureLevelResult],
  );

  const [updateQBankStructureLevels, { loading: isUpdateQBankStructureLevelsLoading }] =
    useUpdateQBankStructureLevelsByQBankStructureIdMutation();

  const submitSetupQBankStructureLevelForm = useCallback(
    async (formValues: QBankStructureLevel[]) => {
      const qBankStructureLevels = formValues.map(({ id, enabled }) => ({
        id,
        enabled,
      }));

      if (qBankStructureId) {
        await updateQBankStructureLevels({
          variables: {
            input: {
              qBankStructureId,
              qBankStructureLevels,
            },
          },
        });
      }
    },
    [updateQBankStructureLevels, qBankStructureLevels, qBankStructureId],
  );

  const contextValue = useMemo(
    () => ({
      createSection,
      setupSection,
      submitCreateQBankStructureForm,
      isCreateQBankStructureLoading,
      qBankStructureLevels,
      isGetQBankStructureLevelLoading,
      qBankStructureId,
      submitSetupQBankStructureLevelForm,
      isUpdateQBankStructureLevelsLoading,
    }),
    [
      createSection,
      setupSection,
      submitCreateQBankStructureForm,
      isCreateQBankStructureLoading,
      qBankStructureLevels,
      isGetQBankStructureLevelLoading,
      qBankStructureId,
      submitSetupQBankStructureLevelForm,
      isUpdateQBankStructureLevelsLoading,
    ],
  );

  const handleClose = () => {
    setAction(null);
  };

  return (
    <QBankStructureCreationModalContext.Provider value={contextValue}>
      {children}
      <CreateQBankStructureModal
        open={action === QBankStructureCreationModalAction.CREATE_SECTION}
        onClose={handleClose}
      />
      <SetupQBankStructureLevelModal
        open={action === QBankStructureCreationModalAction.SETUP_SECTION}
        onClose={handleClose}
      />
    </QBankStructureCreationModalContext.Provider>
  );
}

export { QBankStructureCreationModalContext, QBankStructureCreationModalActionProvider };
